import { Analysis, PossibleRootCauseCategory, ProbabilityCategory } from '.';

export class GlucoseAnalysis extends Analysis {
    glucoseRegulationProbability?: number;
    glucoseRegulationProbabilityCategory?: ProbabilityCategory;

    reset() {
        super.reset();
        this.glucoseRegulationProbability = -1;
        this.glucoseRegulationProbabilityCategory = ProbabilityCategory.NA;
    }

    performAnalysis() {
        super.performAnalysis();
        this.calculateGlucoseRegulationLikelihood();
        this.calculateGlucoseRegulationLikelihoodCategory();
    }

    calculateGlucoseRegulationLikelihood() {
        const ba = this.ba;
        const hasGlucose = ba.glucose?.hasValue?.();
        const hasHbA1c = ba.hemoglobinA1c?.hasValue?.();
        const hasTgHdl = ba.triglyceridesHDLRatio?.hasValue?.();

        if (!hasGlucose && !hasHbA1c && !hasTgHdl) return;

        const gHigh = hasGlucose && ba.glucose.isGreaterThanOptimal();
        const gLab  = hasGlucose && ba.glucose.isGreaterThanLab();
        const hHigh = hasHbA1c  && ba.hemoglobinA1c.isGreaterThanOptimal();
        const hLab  = hasHbA1c  && ba.hemoglobinA1c.isGreaterThanLab();
        const tHigh = hasTgHdl  && ba.triglyceridesHDLRatio.isGreaterThanOptimal();
        const tLab  = hasTgHdl  && ba.triglyceridesHDLRatio.isGreaterThanLab();

        if ((gLab && hLab) || (gLab && tLab) || (hLab && tLab)) {
            this.glucoseRegulationProbability = 95;
        } else if ((gHigh && hHigh) || (gHigh && tHigh) || (hHigh && tHigh) || gLab || hLab) {
            this.glucoseRegulationProbability = 80;
        } else if (gHigh || hHigh || tHigh) {
            this.glucoseRegulationProbability = 60;
        } else {
            this.glucoseRegulationProbability = 25;
        }
    }

    calculateGlucoseRegulationLikelihoodCategory() {
        const p = this.glucoseRegulationProbability;
        if (p === undefined || p < 0) return;
        if (p >= 90) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.HIGHLY_LIKELY;
        } else if (p >= 70) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.LIKELY;
        } else if (p >= 50) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.POSSIBLE;
        } else {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.LESS_LIKELY;
        }
    }

    private addGlucosePatternCategory(category: PossibleRootCauseCategory) {
        if (!this.possibleRootCauseCategories.includes(category)) {
            this.possibleRootCauseCategories.push(category);
        }
    }

    private markerHasValue(marker?: any): boolean {
        return !!marker && !!marker.hasValue && marker.hasValue();
    }

    private isMarkerHigh(marker?: any): boolean {
        if (!this.markerHasValue(marker)) return false;
        return marker.isGreaterThanLab?.() || marker.isGreaterThanOptimal?.() || false;
    }

    private isMarkerLow(marker?: any): boolean {
        if (!this.markerHasValue(marker)) return false;
        return marker.isLessThanLab?.() || marker.isStricklyLessThanOptimal?.() || marker.isLessThanOptimal?.() || false;
    }

    private isMarkerOptimal(marker?: any): boolean {
        return this.markerHasValue(marker) && !this.isMarkerHigh(marker) && !this.isMarkerLow(marker);
    }

    calculateInsights() {
        const ba = this.ba;
        const glucoseHigh = this.isMarkerHigh(ba.glucose);
        const glucoseLow  = this.isMarkerLow(ba.glucose);
        const glucoseOpt  = this.isMarkerOptimal(ba.glucose);
        const hbA1cHigh   = this.isMarkerHigh(ba.hemoglobinA1c);
        const insulinHigh = this.isMarkerHigh(ba.insulin);
        const insulinLow  = this.isMarkerLow(ba.insulin);
        const insulinOpt  = this.isMarkerOptimal(ba.insulin);
        const cpHigh      = this.isMarkerHigh(ba.cpeptide);
        const cpLow       = this.isMarkerLow(ba.cpeptide);
        const cpOpt       = this.isMarkerOptimal(ba.cpeptide);

        const insulinOrCpHigh = insulinHigh || cpHigh;
        const insulinOrCpLow  = insulinLow  || cpLow;
        const insulinOrCpOpt  = insulinOpt  || cpOpt;

        const pickInsulinVariant = (catHigh: PossibleRootCauseCategory, catOpt: PossibleRootCauseCategory, catLow: PossibleRootCauseCategory, catNone: PossibleRootCauseCategory) => {
            if (insulinOrCpHigh) return catHigh;
            if (insulinOrCpOpt)  return catOpt;
            if (insulinOrCpLow)  return catLow;
            return catNone;
        };

        // Primary pattern (most specific first: both glucose + HbA1c high)
        if (glucoseHigh && hbA1cHigh) {
            this.addGlucosePatternCategory(pickInsulinVariant(
                PossibleRootCauseCategory.CATEGORY_72041,
                PossibleRootCauseCategory.CATEGORY_72042,
                PossibleRootCauseCategory.CATEGORY_72043,
                PossibleRootCauseCategory.CATEGORY_72044
            ));
        } else if (glucoseHigh && this.markerHasValue(ba.hemoglobinA1c)) {
            // High glucose + HbA1c present but not high (≤ optimal)
            this.addGlucosePatternCategory(pickInsulinVariant(
                PossibleRootCauseCategory.CATEGORY_72021,
                PossibleRootCauseCategory.CATEGORY_72022,
                PossibleRootCauseCategory.CATEGORY_72023,
                PossibleRootCauseCategory.CATEGORY_72024
            ));
        } else if (glucoseHigh) {
            // High glucose, no HbA1c value
            this.addGlucosePatternCategory(pickInsulinVariant(
                PossibleRootCauseCategory.CATEGORY_72001,
                PossibleRootCauseCategory.CATEGORY_72002,
                PossibleRootCauseCategory.CATEGORY_72003,
                PossibleRootCauseCategory.CATEGORY_72004
            ));
        } else if (hbA1cHigh && this.markerHasValue(ba.glucose)) {
            // HbA1c high + glucose present but not high (≤ optimal)
            this.addGlucosePatternCategory(pickInsulinVariant(
                PossibleRootCauseCategory.CATEGORY_72031,
                PossibleRootCauseCategory.CATEGORY_72032,
                PossibleRootCauseCategory.CATEGORY_72033,
                PossibleRootCauseCategory.CATEGORY_72034
            ));
        } else if (hbA1cHigh) {
            // High HbA1c, no glucose value
            this.addGlucosePatternCategory(pickInsulinVariant(
                PossibleRootCauseCategory.CATEGORY_72011,
                PossibleRootCauseCategory.CATEGORY_72012,
                PossibleRootCauseCategory.CATEGORY_72013,
                PossibleRootCauseCategory.CATEGORY_72014
            ));
        } else if (glucoseLow && !hbA1cHigh) {
            // Low glucose
            this.addGlucosePatternCategory(pickInsulinVariant(
                PossibleRootCauseCategory.CATEGORY_72051,
                PossibleRootCauseCategory.CATEGORY_72052,
                PossibleRootCauseCategory.CATEGORY_72053,
                PossibleRootCauseCategory.CATEGORY_72054
            ));
        }

        // Optimal Glucose + High Insulin (21.x) — independent of primary pattern
        if (glucoseOpt && !hbA1cHigh && insulinOrCpHigh) {
            const lipidHigh = this.isMarkerHigh(ba.triglyceridesHDLRatio) || this.isMarkerHigh(ba.triglycerides);
            this.addGlucosePatternCategory(lipidHigh
                ? PossibleRootCauseCategory.CATEGORY_72061
                : PossibleRootCauseCategory.CATEGORY_72062
            );
        }

        // Add-ons (fire independently of primary pattern)
        const glucoseOrHbA1cHigh = glucoseHigh || hbA1cHigh;
        if (glucoseOrHbA1cHigh &&
            this.isMarkerHigh(ba.ldl) &&
            (this.isMarkerHigh(ba.triglyceridesHDLRatio) ||
             (this.isMarkerHigh(ba.triglycerides) && !this.isMarkerHigh(ba.hdl)))) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72071);
        }

        if (glucoseOrHbA1cHigh &&
            this.isMarkerHigh(ba.alt) &&
            (this.isMarkerHigh(ba.triglyceridesHDLRatio) || this.isMarkerHigh(ba.triglycerides))) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72072);
        }

        if (glucoseOrHbA1cHigh && this.isMarkerLow(ba.carbonDioxide)) {
            this.addGlucosePatternCategory(this.isMarkerHigh(ba.bun)
                ? PossibleRootCauseCategory.CATEGORY_72074
                : PossibleRootCauseCategory.CATEGORY_72073
            );
        }

        if (glucoseOrHbA1cHigh &&
            this.markerHasValue(ba.insulin) && !insulinHigh &&
            this.markerHasValue(ba.cpeptide) && !cpHigh) {
            const kLow = this.isMarkerLow(ba.potassium);
            const caLow = this.isMarkerLow(ba.calcium);
            if (kLow && caLow)      this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72075);
            else if (kLow)          this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72076);
            else if (caLow)         this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72077);
            else                    this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72078);
        }

        if (glucoseLow) {
            if (this.isMarkerHigh(ba.bun) &&
                !(this.markerHasValue(ba.creatinine) && this.isMarkerHigh(ba.creatinine))) {
                this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72081);
            }
            if (this.isMarkerLow(ba.cortisolAM) || this.isMarkerLow(ba.cortisolPM)) {
                this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72082);
            }
            const b6Low  = this.markerHasValue(ba.vitaminB6) && this.isMarkerLow(ba.vitaminB6);
            const altLow = this.isMarkerLow(ba.alt);
            const astLow = this.isMarkerLow(ba.ast);
            if (b6Low) {
                this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72083);
            } else if (altLow && astLow) {
                this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72084);
            } else if (altLow) {
                this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72085);
            } else if (astLow) {
                this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_72086);
            }
        }

        // --- Existing single-marker categories ---
        if (this.ba.glucose.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6000);
        } else if (this.ba.glucose.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6010);
        }

        if (this.ba.hemoglobinA1c.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6020);
        } else if (this.ba.hemoglobinA1c.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6030);
        }

        if (this.ba.insulin.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6040);
        } else if (this.ba.insulin.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6050);
        }

        if (this.ba.cpeptide.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6060);
        } else if (this.ba.cpeptide.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6070);
        }

        if (
          this.ba.triglycerides.isGreaterThanOptimal() &&
          this.ba.triglyceridesHDLRatio.isLessThanOrIsOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6075,
          );
        } else if (
          this.ba.triglycerides.isGreaterThanOptimal() &&
          this.ba.triglyceridesHDLRatio.isGreaterThanOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6076,
          );
        } else if (
          this.ba.triglycerides.isLessThanOrIsOptimal() &&
          this.ba.triglyceridesHDLRatio.isGreaterThanOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6077,
          );
        }

        if (this.ba.amylase.isGreaterThanOptimal()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6080);
        }

        if (this.ba.amylase.isGreaterThanLab()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6085);
        }

        if (this.ba.amylase.isLessThanOptimal()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6090);
        }

        if (this.ba.amylase.isLessThanLab()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6091);
        }

    }
}